/**
 * AuthTest Component
 * Test component to verify AuthRepository functionality
 */

import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { Button } from './ui/button';

const AuthTest = () => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    apiKey: null
  });
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);

  // Check initial auth state
  useEffect(() => {
    const checkAuthState = () => {
      const currentUser = authService.getCurrentUser();
      const isAuthenticated = authService.isLoggedIn();
      const apiKey = authService.getCurrentApiKey();

      setAuthState({
        isAuthenticated,
        user: currentUser,
        apiKey
      });
    };

    checkAuthState();
  }, []);

  const addTestResult = (test, result, details = '') => {
    setTestResults(prev => [...prev, {
      test,
      result,
      details,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  const runAuthTests = async () => {
    setIsRunning(true);
    setTestResults([]);

    try {
      // Test 1: Check current user
      addTestResult('getCurrentUser', 'RUNNING', 'Checking current user...');
      const currentUser = authService.getCurrentUser();
      addTestResult('getCurrentUser', currentUser ? 'PASS' : 'FAIL', 
        currentUser ? `User: ${currentUser.email}` : 'No user found');

      // Test 2: Check if logged in
      addTestResult('isLoggedIn', 'RUNNING', 'Checking login status...');
      const isLoggedIn = authService.isLoggedIn();
      addTestResult('isLoggedIn', isLoggedIn ? 'PASS' : 'FAIL', 
        `Logged in: ${isLoggedIn}`);

      // Test 3: Get current API key
      addTestResult('getCurrentApiKey', 'RUNNING', 'Retrieving API key...');
      const apiKey = authService.getCurrentApiKey();
      addTestResult('getCurrentApiKey', apiKey ? 'PASS' : 'FAIL', 
        apiKey ? `API Key length: ${apiKey.length}` : 'No API key found');

      // Test 4: Validate token (if user is logged in)
      if (isLoggedIn) {
        addTestResult('validateToken', 'RUNNING', 'Validating authentication token...');
        const tokenResult = await authService.validateToken();
        addTestResult('validateToken', tokenResult.valid ? 'PASS' : 'FAIL', 
          tokenResult.valid ? 'Token is valid' : `Error: ${tokenResult.error}`);
      }

    } catch (error) {
      addTestResult('runAuthTests', 'ERROR', `Test suite error: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const testEmailLogin = async () => {
    const testEmail = 'test@example.com';
    const testPassword = 'testpassword';

    setIsRunning(true);
    addTestResult('emailLogin', 'RUNNING', `Testing enhanced email login with ${testEmail}...`);

    try {
      await authService.signInWithEmail(testEmail, testPassword);
      addTestResult('emailLogin', 'PASS', 'Email login successful');

      // Test API key retrieval
      const apiKey = localStorage.getItem('PROVIDER_API_KEY');
      addTestResult('apiKeyCheck', apiKey ? 'PASS' : 'FAIL',
        apiKey ? `API key stored (length: ${apiKey.length})` : 'No API key found');

    } catch (error) {
      addTestResult('emailLogin', 'EXPECTED_FAIL', `Expected error: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const testGoogleLogin = async () => {
    setIsRunning(true);
    addTestResult('googleLogin', 'RUNNING', 'Testing Google login...');
    
    try {
      await authService.signInWithGoogle();
      addTestResult('googleLogin', 'PASS', 'Google login successful');
    } catch (error) {
      addTestResult('googleLogin', 'FAIL', `Google login error: ${error.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const testSignOut = async () => {
    setIsRunning(true);
    addTestResult('signOut', 'RUNNING', 'Testing enhanced sign out with API key cleanup...');

    // Check if API key exists before sign out
    const apiKeyBefore = localStorage.getItem('PROVIDER_API_KEY');
    addTestResult('preSignOutCheck', apiKeyBefore ? 'INFO' : 'INFO',
      `API key before sign out: ${apiKeyBefore ? 'Present' : 'Not present'}`);

    try {
      await authService.signOut();

      // Check if API key was properly cleaned up
      const apiKeyAfter = localStorage.getItem('PROVIDER_API_KEY');
      addTestResult('signOut', 'PASS', 'Sign out successful');
      addTestResult('apiKeyCleanup', !apiKeyAfter ? 'PASS' : 'FAIL',
        `API key cleanup: ${!apiKeyAfter ? 'Success' : 'Failed - still present'}`);

    } catch (error) {
      addTestResult('signOut', 'FAIL', `Sign out error: ${error.message}`);

      // Still check cleanup even if sign out failed
      const apiKeyAfter = localStorage.getItem('PROVIDER_API_KEY');
      addTestResult('emergencyCleanup', !apiKeyAfter ? 'PASS' : 'FAIL',
        `Emergency cleanup: ${!apiKeyAfter ? 'Success' : 'Failed'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">AuthRepository Test Suite</h2>
      
      {/* Current Auth State */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Current Authentication State</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="font-medium">Status:</span>
            <span className={`ml-2 px-2 py-1 rounded text-xs ${
              authState.isAuthenticated ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {authState.isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </span>
          </div>
          <div>
            <span className="font-medium">User:</span>
            <span className="ml-2">{authState.user?.email || 'None'}</span>
          </div>
          <div>
            <span className="font-medium">API Key:</span>
            <span className="ml-2">{authState.apiKey ? '✓ Present' : '✗ Missing'}</span>
          </div>
        </div>
      </div>

      {/* Test Controls */}
      <div className="mb-6 flex flex-wrap gap-3">
        <Button 
          onClick={runAuthTests} 
          disabled={isRunning}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isRunning ? 'Running...' : 'Run Auth Tests'}
        </Button>
        <Button 
          onClick={testEmailLogin} 
          disabled={isRunning}
          variant="outline"
        >
          Test Email Login
        </Button>
        <Button 
          onClick={testGoogleLogin} 
          disabled={isRunning}
          variant="outline"
        >
          Test Google Login
        </Button>
        {authState.isAuthenticated && (
          <Button 
            onClick={testSignOut} 
            disabled={isRunning}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            Test Sign Out
          </Button>
        )}
        <Button 
          onClick={clearResults} 
          disabled={isRunning}
          variant="ghost"
        >
          Clear Results
        </Button>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold mb-3">Test Results</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {testResults.map((result, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-white rounded border">
                <span className="text-xs text-gray-500 min-w-[60px]">{result.timestamp}</span>
                <span className="font-medium min-w-[120px]">{result.test}</span>
                <span className={`px-2 py-1 rounded text-xs font-medium min-w-[80px] text-center ${
                  result.result === 'PASS' ? 'bg-green-100 text-green-800' :
                  result.result === 'FAIL' ? 'bg-red-100 text-red-800' :
                  result.result === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                  result.result === 'EXPECTED_FAIL' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {result.result}
                </span>
                <span className="text-sm text-gray-600 flex-1">{result.details}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthTest;
